import React, { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { Copy, Trash2 } from 'lucide-react'

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text || '')
    toast.success(`${label} copied`)
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`)
  }
}

// formulario.html is consumer-facing and branded/served on the vertical's
// own public domain, NOT the CRM's admin domain — it is not reverse-proxied
// alongside /api the way the rest of the CRM/API is. There's currently only
// one such domain in production; if that changes (multiple verticals with
// different branded domains), this needs to become per-campaign data from
// the backend instead of a constant.
const FORM_ORIGIN = 'https://bestpersonalloansnearme.com'

/**
 * Manages the single TapfiliateConfig (is_active/suppress_if_zero) for one
 * affiliate_clicks-intake campaign — GET/PUT/DELETE /campaigns/{id}/tapfiliate.
 * There's one Tapfiliate account org-wide; its api_key lives in a backend env
 * var, never in this resource or its API responses — nothing here to mask,
 * reveal, or copy. Unlike ClickPartnerMapping this is a 1:1 resource per
 * campaign, not a list, and Tapfiliate does its own click attribution.
 *
 * On first save for a campaign, PUT also provisions the campaign's affiliate
 * as a real Tapfiliate affiliate server-side (postbacks/tapfiliate_provisioning.py)
 * and returns their affiliate_referral_code — there's no manual "enter the
 * referral code" field anywhere in this flow. Provisioning requires
 * AffiliateProfile.email to be set (see PartnersPage.jsx's affiliate form);
 * if it isn't, PUT 409s if the campaign isn't affiliate_clicks intake, or
 * 422s if provisioning itself fails (blank email, or a backend deploy-config
 * issue) — both are surfaced as an inline banner rather than a generic toast
 * so the user gets a clear, retryable explanation instead of a raw
 * failed-request state.
 */
export default function TapfiliateConfigModal({ campaignId, campaignName, affiliateName, onClose }) {
  const [loading, setLoading] = useState(true)
  const [config, setConfig] = useState(null)
  const [form, setForm] = useState({ is_active: true, suppress_if_zero: true })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [saveError, setSaveError] = useState('')

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    setSaveError('')
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/tapfiliate`)
      setConfig(data)
      setForm({ is_active: data.is_active, suppress_if_zero: data.suppress_if_zero })
    } catch (err) {
      if (err.response?.status === 404) {
        setConfig(null)
        setForm({ is_active: true, suppress_if_zero: true })
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.detail || 'Error loading Tapfiliate config')
      }
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => { fetchConfig() }, [fetchConfig])

  const save = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveError('')
    try {
      const { data } = await api.put(`/campaigns/${campaignId}/tapfiliate`, {
        is_active: form.is_active,
        suppress_if_zero: form.suppress_if_zero,
      })
      setConfig(data)
      setForm({ is_active: data.is_active, suppress_if_zero: data.suppress_if_zero })
      toast.success('Tapfiliate config saved')
    } catch (err) {
      if (err.response?.status === 409) {
        setSaveError(
          err.response?.data?.message ||
          'This campaign is not an affiliate_clicks-intake campaign — switch its Intake Type before configuring Tapfiliate.'
        )
      } else if (err.response?.status === 422) {
        const who = affiliateName ? `"${affiliateName}"` : 'this campaign\'s affiliate'
        setSaveError(
          (err.response?.data?.message || `Could not provision ${who} in Tapfiliate.`) +
          ` Check ${who}'s email is set on their affiliate profile (Partners page), then retry — this save is safe to repeat.`
        )
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.detail || 'Error saving Tapfiliate config')
      }
    } finally {
      setSaving(false)
    }
  }

  const deleteConfig = async () => {
    setDeleting(true)
    try {
      await api.delete(`/campaigns/${campaignId}/tapfiliate`)
      toast.success('Tapfiliate config deleted')
      setConfirmDelete(false)
      setConfig(null)
      setForm({ is_active: true, suppress_if_zero: true })
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Error deleting Tapfiliate config')
    } finally {
      setDeleting(false)
    }
  }

  const referralCode = config?.affiliate_referral_code || ''
  const clickUrl = campaignId
    ? `${FORM_ORIGIN}/?${referralCode ? `ref=${referralCode}&` : ''}cid=${campaignId}`
    : ''

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Tapfiliate{campaignName ? ` · ${campaignName}` : ''}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Your Tapfiliate click URL</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" readOnly value={clickUrl} style={{ fontFamily: 'monospace', fontSize: 12 }} />
              <button
                type="button"
                className="btn btn-ghost btn-icon btn-sm"
                title="Copy"
                disabled={!referralCode}
                onClick={() => copyToClipboard(clickUrl, 'Click URL')}
              >
                <Copy size={14} />
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              {referralCode
                ? "This affiliate's referral code is filled in automatically — send this URL to them directly, no Tapfiliate dashboard steps needed."
                : 'Save this config to provision the affiliate in Tapfiliate and fill in their referral code here.'}
            </div>
          </div>

          {loading ? (
            <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
          ) : (
            <>
              {saveError && (
                <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, border: '1px solid var(--red)', color: 'var(--red)', fontSize: 13 }}>
                  {saveError}
                </div>
              )}

              {!config && !saveError && (
                <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                  No Tapfiliate config for this campaign yet. Saving will provision the affiliate in Tapfiliate.
                </p>
              )}

              <form onSubmit={save}>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="select" value={String(form.is_active)} onChange={e => setForm(p => ({ ...p, is_active: e.target.value === 'true' }))}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Suppress if zero</label>
                  <select className="select" value={String(form.suppress_if_zero)} onChange={e => setForm(p => ({ ...p, suppress_if_zero: e.target.value === 'true' }))}>
                    <option value="true">Suppress $0 conversions</option>
                    <option value="false">Send $0 conversions</option>
                  </select>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Only applies to sold leads that resolve to a $0 payout.
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
                  <div>
                    {config && (
                      confirmDelete ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Delete config?</span>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
                          <button type="button" className="btn btn-danger btn-sm" disabled={deleting} onClick={deleteConfig}>Delete</button>
                        </div>
                      ) : (
                        <button type="button" className="btn btn-danger btn-icon btn-sm" title="Delete config" onClick={() => setConfirmDelete(true)}>
                          <Trash2 size={14} />
                        </button>
                      )
                    )}
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? <div className="spinner" /> : (config ? 'Save changes' : 'Create config')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
